import React, { useState, useEffect } from 'react'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import { Badge } from '../components/Badge'
import { useOpenRFQs, useSellerProvisionings } from '../hooks/useQuoteRequests'
import { popLabel } from '../constants/pops'
import type { Buyer, RfqSellerView, ProvisioningView } from '../api/schema'
import { ProvisioningTimeline } from '../components/ProvisioningTimeline'
import { acknowledgeProvisioningSubmission, declineProvisioningSubmission, getBuyerDetails, getSellerProvisioning, getSellerRFQ, markProvisioningFailed, markProvisioningReady, sellerDownloadProvisioningLoa } from '../api/api-client'


function formatProtectionPeriod(rfq: RfqSellerView): string {
  const frmt = (d: Date) =>
    d.toLocaleDateString('en-SG', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
  const from = new Date(rfq.protection_period_from)
  const until = new Date(rfq.protection_period_until)
  return `${frmt(from)} → ${frmt(until)}`
}

function formatProvisioningPeriod(prov: ProvisioningView): string {
  const frmt = (d: Date) =>
    d.toLocaleDateString('en-SG', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
  const from = new Date(prov.period_from)
  const until = new Date(prov.period_until)
  return `${frmt(from)} → ${frmt(until)}`
}


// ─── Listing row ──────────────────────────────────────────────────────────────

const FlatRfqRow: React.FC<{ rfq: RfqSellerView; onClick: () => void }> = ({ rfq, onClick }) => (
  <tr style={{ borderBottom: '1px solid var(--color-border-tertiary)' }} onClick={onClick}
    onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => { e.currentTarget.style.background = 'var(--color-background-secondary)' }}
    onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => { e.currentTarget.style.background = 'transparent' }}

  >
    <td style={{
      padding: '0.75rem', fontWeight: 500, fontSize: '0.875rem',
      color: 'var(--color-text-primary)'
    }}>
      {rfq.rfq_id}
    </td>
    <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-primary)' }}>
      {rfq.buyer_name ?? '—'}
    </td>
    <td style={{
      padding: '0.75rem', fontSize: '0.75rem',
      fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap'
    }}>
      {formatProtectionPeriod(rfq)}
    </td>
    <td style={{
      padding: '0.75rem', fontSize: '0.75rem',
      color: 'var(--color-text-secondary)'
    }}>
      {rfq.request_locations.length}
    </td>
    <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
      {rfq.request_locations.reduce((s, l) => s + l.dirty_bandwidth_gbps, 0)} Gbps
    </td>
    <td style={{
      padding: '0.75rem', fontSize: '0.8rem',
      color: 'var(--color-text-success)', fontWeight: 500
    }}>
      {rfq.request_locations.reduce((s, l) => s + l.clean_bandwidth_gbps, 0)} Gbps
    </td>
    <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
      {rfq.quote ? `$${(rfq.quote.total_net_price).toLocaleString()} USD` : '—'}
    </td>

    <td style={{ padding: '0.75rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Badge status={rfq.status} />
        {rfq.has_quoted && !rfq.has_declined_rfq && (
          <span style={{ fontSize: '0.65rem', color: '#0D8F7A', fontWeight: 600 }}>Quoted</span>
        )}
        {rfq.has_declined_rfq && (
          <span style={{ fontSize: '0.65rem', color: '#EF4444', fontWeight: 600 }}>Declined</span>
        )}
      </div>
    </td>
    <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
      View →
    </td>
  </tr>
)



// ─── Detail view ──────────────────────────────────────────────────────────────

const RFQDetail: React.FC<{
  row: RfqSellerView
  onBack: () => void
  onRefresh: () => Promise<void>
}> = ({ row, onBack, onRefresh }) => {
  const [buyer, setBuyer] = useState<Buyer | null>(null)
  const [buyerLoading, setBuyerLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setRefreshing(false)
    }
  }


  useEffect(() => {
    setBuyerLoading(true)
    getBuyerDetails(row.buyer_id ?? '')
      .then(setBuyer)
      .catch(() => setBuyer(null))
      .finally(() => setBuyerLoading(false))
  }, [row.buyer_id])


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Back + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.375rem 0.75rem', border: '1px solid var(--color-border-secondary)',
          borderRadius: '0.375rem', background: 'var(--color-background-primary)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)', cursor: 'pointer'
        }}>
          <ArrowLeft size={13} /> Back
        </button>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
          RFQ {row.rfq_id}{row.buyer_name && ` — ${row.buyer_name}`}
        </h2>
        <Badge status={row.status} />
        <button onClick={handleRefresh} disabled={refreshing} style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem', marginLeft: 'auto',
          padding: '0.375rem 0.75rem', border: '1px solid var(--color-border-secondary)',
          borderRadius: '0.375rem', background: 'var(--color-background-primary)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)',
          cursor: refreshing ? 'not-allowed' : 'pointer'
        }}>
          <RefreshCw size={13} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
        {[
          { label: 'Protection Period', value: formatProtectionPeriod(row) },
          { label: 'Status', value: row.status },
          { label: 'Buyer', value: row.buyer_name ?? '—' },
          { label: 'Locations', value: String(row.request_locations.length) },
        ].map(({ label, value }) => (
          <div key={label} style={{
            background: 'var(--color-background-primary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: '0.5rem', padding: '0.75rem 1rem',
          }}>
            <div style={{
              fontSize: '0.68rem', color: 'var(--color-text-tertiary)',
              fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em',
              marginBottom: '0.25rem'
            }}>{label}</div>
            <div style={{
              fontSize: '0.875rem', fontWeight: 600,
              color: 'var(--color-text-primary)'
            }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Requested Locations */}
      <div style={{ ...card, padding: 0 }}>
        <div style={{
          padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border-secondary)',
          fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)'
        }}>
          Requested Locations
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{
              fontSize: '0.75rem', color: 'var(--color-text-secondary)',
              borderBottom: '1px solid var(--color-border-secondary)',
              background: 'var(--color-background-secondary)'
            }}>
              {['Location', 'Dirty Bandwidth', 'Clean Bandwidth'].map(h => (
                <th key={h} style={{
                  padding: '0.625rem 0.75rem', textAlign: 'left',
                  fontWeight: 500, whiteSpace: 'nowrap'
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {row.request_locations.map((loc, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                <td style={{
                  padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                  color: 'var(--color-text-primary)'
                }}>
                  {popLabel(loc.location_id)}
                </td>
                <td style={{
                  padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                  color: 'var(--color-text-secondary)'
                }}>
                  {loc.dirty_bandwidth_gbps} Gbps
                </td>
                <td style={{
                  padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                  color: 'var(--color-text-success)', fontWeight: 500
                }}>
                  {loc.clean_bandwidth_gbps} Gbps
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Your Quote */}
      {row.quote && (
        <div style={{ ...card, padding: 0 }}>
          <div style={{
            padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border-secondary)',
            fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)'
          }}>
            Your Quote
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{
                fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                borderBottom: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-secondary)'
              }}>
                {['Location', 'Dirty Bandwidth', 'Dirty Price', 'Clean Bandwidth', 'Clean Price'].map(h => (
                  <th key={h} style={{
                    padding: '0.625rem 0.75rem', textAlign: 'left',
                    fontWeight: 500, whiteSpace: 'nowrap'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {row.quote.locations.map((loc, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                  <td style={{
                    padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                    color: 'var(--color-text-primary)'
                  }}>
                    {popLabel(loc.location_id)}
                  </td>
                  <td style={{
                    padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {loc.dirty_bandwidth_gbps} Gbps
                  </td>
                  <td style={{
                    padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {`$${(loc.dirty_bandwidth_total_price).toLocaleString()} USD`}
                  </td>
                  <td style={{
                    padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                    color: 'var(--color-text-success)', fontWeight: 500
                  }}>
                    {loc.clean_bandwidth_gbps} Gbps
                  </td>
                  <td style={{
                    padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {`$${(loc.clean_bandwidth_total_price).toLocaleString()} USD`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <hr></hr>

          <div style={{
            padding: '0.75rem 1rem', borderTop: '1px solid var(--color-border-secondary)',
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem',
          }}>
            <div>
              <div style={fieldLabel}>Net Total</div>
              {`$${(row.quote.total_net_price).toLocaleString()} USD`}
            </div>
            <div>
              <div style={fieldLabel}>Transaction Fees</div>
              {`$${(row.quote.transaction_fees).toLocaleString()} USD`}
            </div>
            <div>
              <div style={fieldLabel}>Gross Total</div>
              <div style={{ ...fieldValue, color: '#0D8F7A' }}>
                {`$${(row.quote.total_gross_price).toLocaleString()} USD`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buyer Details — always shown */}
      {buyerLoading ? (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
          Loading buyer details…
        </p>
      ) : buyer ? (
        <>
          <div style={card}>
            <div style={sectionTitle}>Buyer Details</div>
            <div style={grid3}>
              <Field label="Company" value={buyer.company.name} />
              <Field label="Nature of Business" value={buyer.company.nature_of_business} />
              <Field label="Location" value={`${buyer.company.address.city}, ${buyer.company.address.country_code}`} />
              <Field label="Commercial Contact" value={buyer.commercial_contact.full_name} />
              <Field label="Email" value={buyer.commercial_contact.email} />
              <Field label="Phone" value={buyer.commercial_contact.phone ?? '—'} />
              <Field label="Technical Contact" value={buyer.primary_technical_contact.full_name} />
              <Field label="Tech Email" value={buyer.primary_technical_contact.email} />
              <Field label="Tech Phone" value={buyer.primary_technical_contact.phone ?? '—'} />
            </div>
          </div>

        </>
      ) : (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
          No buyer details available.
        </p>
      )}

    </div>
  )
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={fieldLabel}>{label}</div>
      <div style={fieldValue}>{value || '—'}</div>
    </div>
  )
}

const sectionTitle: React.CSSProperties = {
  fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-tertiary)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  marginBottom: '1rem', paddingBottom: '0.5rem',
  borderBottom: '1px solid var(--color-border-tertiary)',
}
const grid3: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem',
}
const fieldLabel: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-tertiary)',
  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem',
}
const fieldValue: React.CSSProperties = {
  fontSize: '0.875rem', color: 'var(--color-text-primary)',
  fontWeight: 500, wordBreak: 'break-all',
}
// const pill: React.CSSProperties = {
//   padding: '0.25rem 0.625rem', borderRadius: '0.375rem',
//   background: 'var(--color-background-secondary)',
//   border: '1px solid var(--color-border-secondary)',
//   fontSize: '0.8rem', color: 'var(--color-text-primary)',
//   fontFamily: 'var(--font-mono)',
// }

// ─── Page ─────────────────────────────────────────────────────────────────────

export const SellerDashboard: React.FC = () => {
  const { rows, loading, error, refresh } = useOpenRFQs()
  const { provisionings, loading: provLoading } = useSellerProvisionings()
  const [selectedRfq, setSelectedRfq] = useState<RfqSellerView | null>(null)
  const [selectedProv, setSelectedProv] = useState<ProvisioningView | null>(null)
  const [provRefreshKey, setProvRefreshKey] = useState(0)
  const [activeTab, setActiveTab] = useState<'rfqs' | 'provisioning'>('rfqs')

  const myRfqRows = rows

  const pending_rfqs = myRfqRows.filter(r =>
    r.status === 'open'
  ).length

  const pending_provisionings = provisionings.filter(p =>
    p.status !== "completed"
    && p.status !== "failed"
    && p.submissions.some(s => s.status === 'submitted')
  ).length

  const pendingTotal = pending_rfqs + pending_provisionings

  if (selectedRfq) {
    return (
      <RFQDetail
        row={selectedRfq}
        onBack={() => setSelectedRfq(null)}
        onRefresh={async () => setSelectedRfq(await getSellerRFQ(selectedRfq.id))}
      />
    )
  }

  if (selectedProv) {
    return (
      <ProvisioningTimeline
        key={provRefreshKey}
        onRefresh={async () => {
          setSelectedProv(await getSellerProvisioning(selectedProv.id))
          setProvRefreshKey(k => k + 1)
        }}
        provisioningId={selectedProv.id}
        status={selectedProv.status}
        location={popLabel(selectedProv.location_id)}
        dirtyBw={selectedProv.dirty_bandwidth_gbps}
        cleanBw={selectedProv.clean_bandwidth_gbps}
        periodFrom={selectedProv.period_from}
        periodUntil={selectedProv.period_until}
        history={selectedProv.submissions.map(s => ({
          id: s.id,
          submitted_at: s.submitted_at,
          status: s.status,
          decline_reason: s.decline_reason,
          documents: [],
          details: s.details,
        }))}
        role="seller"
        showHeader={true}
        onBack={() => setSelectedProv(null)}
        onAcknowledge={async (submissionId) => {
          acknowledgeProvisioningSubmission(selectedProv.id, submissionId)
        }}
        onDecline={async (submissionId, reason) => {
          declineProvisioningSubmission(selectedProv.id, submissionId, reason)
        }}
        onReady={async (submissionId) => {
          markProvisioningReady(selectedProv.id, submissionId)
        }}
        onFail={async (submissionId, reason) => {
          markProvisioningFailed(selectedProv.id, submissionId, reason)
        }}
        downloadLoa={async (submissionId: number) => sellerDownloadProvisioningLoa(selectedProv.id, submissionId)}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
          Dashboard
          {pendingTotal > 0 && (
            <span style={{
              marginLeft: '0.5rem', padding: '0.15rem 0.5rem',
              borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600,
              background: '#FFF7ED', color: '#C2410C',
            }}>
              {pendingTotal} action{pendingTotal !== 1 ? 's' : ''} needed
            </span>
          )}
        </h2>
        <button onClick={refresh} style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.375rem 0.75rem', border: '1px solid var(--color-border-secondary)',
          borderRadius: '0.375rem', background: 'var(--color-background-primary)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)', cursor: 'pointer'
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{
          borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.875rem',
          background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA'
        }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border-secondary)' }}>
        {([
          { key: 'rfqs', label: 'RFQs', count: pending_rfqs },
          { key: 'provisioning', label: 'Provisioning', count: pending_provisionings },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.625rem 1.25rem', border: 'none', background: 'transparent',
              fontSize: '0.875rem', fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
              borderBottom: activeTab === tab.key ? '2px solid #0D8F7A' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                marginLeft: '0.375rem', padding: '0.1rem 0.4rem',
                borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 700,
                background: '#FFF7ED', color: '#C2410C',
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Open RFQs tab ── */}
      {activeTab === 'rfqs' && (
        <div style={{ ...card, padding: 0 }}>
          {loading && (
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', padding: '1.25rem' }}>
              Loading…
            </p>
          )}
          {!loading && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{
                  fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                  borderBottom: '1px solid var(--color-border-secondary)',
                  background: 'var(--color-background-secondary)'
                }}>
                  {['RFQ ID', 'Buyer', 'Protection Period', 'Locations',
                    'Dirty Bandwidth', 'Clean Bandwidth', 'Quoted Price', 'Status', ''].map(h => (
                      <th key={h} style={{
                        padding: '0.625rem 0.75rem', textAlign: 'left',
                        fontWeight: 500, whiteSpace: 'nowrap'
                      }}>{h}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {myRfqRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{
                      padding: '2rem', textAlign: 'center',
                      fontSize: '0.75rem', color: 'var(--color-text-tertiary)'
                    }}>
                      No RFQs for your locations yet.
                    </td>
                  </tr>
                ) : (
                  myRfqRows.map(rfq => (
                    <FlatRfqRow
                      key={rfq.id}
                      rfq={rfq}
                      onClick={() => setSelectedRfq(rfq)}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Provisioning tab ── */}
      {activeTab === 'provisioning' && (
        <div style={{ ...card, padding: 0 }}>
          {provLoading && (
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', padding: '1.25rem' }}>
              Loading…
            </p>
          )}
          {!provLoading && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{
                  fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                  borderBottom: '1px solid var(--color-border-secondary)',
                  background: 'var(--color-background-secondary)'
                }}>
                  {['Buyer', 'Location', 'Protection Period', 'Dirty Bandwidth',
                    'Clean Bandwidth', 'Provisioning', 'Latest Submission', ''].map(h => (
                      <th key={h} style={{
                        padding: '0.625rem 0.75rem', textAlign: 'left',
                        fontWeight: 500, whiteSpace: 'nowrap'
                      }}>{h}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {provisionings.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{
                      padding: '2rem', textAlign: 'center',
                      fontSize: '0.75rem', color: 'var(--color-text-tertiary)'
                    }}>
                      No provisioning records yet.
                    </td>
                  </tr>
                ) : (
                  provisionings.map(prov => {
                    const latestSub = prov.submissions[prov.submissions.length - 1]
                    return (
                      <tr key={prov.id}
                        onClick={() => setSelectedProv(prov)}
                        onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => {
                          e.currentTarget.style.background = 'var(--color-background-secondary)'
                        }}
                        onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => {
                          e.currentTarget.style.background = 'transparent'
                        }}
                        style={{ borderBottom: '1px solid var(--color-border-tertiary)', cursor: 'pointer' }}
                      >
                        <td style={{
                          padding: '0.75rem', fontWeight: 500, fontSize: '0.875rem',
                          color: 'var(--color-text-primary)'
                        }}>
                          {prov.buyer_name}
                        </td>
                        <td style={{
                          padding: '0.75rem', fontSize: '0.8rem',
                          color: 'var(--color-text-primary)'
                        }}>
                          {popLabel(prov.location_id)}
                        </td>
                        <td style={{
                          padding: '0.75rem', fontSize: '0.75rem',
                          fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatProvisioningPeriod(prov)}
                        </td>
                        <td style={{
                          padding: '0.75rem', fontSize: '0.8rem',
                          color: 'var(--color-text-secondary)'
                        }}>
                          {prov.dirty_bandwidth_gbps} Gbps
                        </td>
                        <td style={{
                          padding: '0.75rem', fontSize: '0.8rem',
                          color: 'var(--color-text-success)', fontWeight: 500
                        }}>
                          {prov.clean_bandwidth_gbps} Gbps
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <Badge status={prov.status} />
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {latestSub ? (
                            <Badge status={latestSub.status} />
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                              No submissions yet
                            </span>
                          )}
                        </td>
                        <td style={{
                          padding: '0.75rem', fontSize: '0.75rem',
                          color: 'var(--color-text-tertiary)'
                        }}>
                          View →
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  )
}

const card: React.CSSProperties = {
  background: '#fff', borderRadius: '0.75rem',
  border: '1px solid #E2E8F0', padding: '1.25rem',
}